import React from 'react';
import {
  Box,
  Text,
  Badge,
  Button,
  Avatar,
  Flex,
  useBreakpointValue,
} from '@chakra-ui/react';
import { FaDollarSign } from 'react-icons/fa';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import UserService from '../../utils/UserService';
import { setCurrentDealFlow as setCurrentDealFlowStore } from '../../redux-toolkit/reducers/dealFlowSlice';

const DealCard = ({ deal }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const dealPadding = useBreakpointValue({ base: 4, sm: 4, md: 5, lg: 5 });
  const fontSize = useBreakpointValue({ base: 'xs', sm: 'sm', md: 'md' });
  const titleFontSize = useBreakpointValue({
    base: 'lg',
    sm: 'lg',
    md: 'lg',
    lg: 'xl',
  });
  const metricsFontSize = useBreakpointValue({
    base: 'md',
    sm: 'lg',
    md: 'lg',
    lg: 'xl',
  });
  const buttonSize = useBreakpointValue({ base: 'sm', sm: 'md', md: 'lg' });
  const avatarSize = useBreakpointValue({ base: 'md', sm: 'md', md: 'lg' });

  const { company, user } = deal;

  const handleSubmitTermsheet = () => {
    dispatch(setCurrentDealFlowStore(deal));
    UserService.setCurrentDealFlow(deal);

    if (deal?.status === 'signed' || deal?.status === 'closed') {
      navigate('../view-post-agreement-stage');
    } else {
      navigate('../view-term-sheet');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Box
      p={dealPadding}
      borderWidth="1px"
      borderRadius={{ base: 'lg', md: 'xl' }}
      bg="white"
      position="relative"
      boxShadow={{ base: 'sm', md: 'md' }}
      className="border border-gray-200 bg-white"
    >
      {/* Header with Company and Founder */}
      <Flex
        alignItems="flex-start"
        mb={{ base: 4, sm: 4, md: 5 }}
        flexDirection={{ base: 'column', sm: 'row' }}
        gap={{ base: 2, sm: 0 }}
      >
        <Box flex={1} w="100%">
          <Text
            fontSize={titleFontSize}
            fontWeight="600"
            color="gray.800"
            lineHeight={{ base: '1.2', md: '1.4' }}
          >
            {company.title}
          </Text>
          <Flex
            gap={{ base: 1, sm: 2 }}
            flexDirection="row"
            alignItems="center"
            flexWrap="wrap"
          >
            <Text fontSize={{ base: 'xs', sm: 'sm' }} color="gray.500">
              {deal.stage} Round
            </Text>
            <Badge
              colorScheme="green"
              size={{ base: 'sm', md: 'md' }}
              borderRadius="full"
              fontSize={{ base: 'xs', sm: 'sm' }}
              px={{ base: 2, sm: 3 }}
              py={{ base: 1, sm: 1 }}
            >
              Raising Funds
            </Badge>
          </Flex>
        </Box>
      </Flex>
      {/* Founder Info */}
      <Flex
        alignItems="center"
        mb={{ base: 4, sm: 4, md: 5 }}
        p={{ base: 3, sm: 3, md: 4 }}
        bg="gray.50"
        borderRadius="lg"
        flexDirection={{ base: 'column', sm: 'row' }}
        textAlign={{ base: 'center', sm: 'left' }}
        gap={{ base: 3, sm: 0 }}
      >
        <Avatar
          size={avatarSize}
          src={user.profile_image}
          name={`${user.first_name} ${user.last_name}`}
          mr={{ base: 0, sm: 3 }}
          mb={{ base: 0, sm: 0 }}
          border="2px solid white"
          boxShadow="md"
        />
        <Box textAlign={{ base: 'center', sm: 'left' }}>
          <Text
            fontSize={{ base: 'sm', sm: 'sm', md: 'md' }}
            fontWeight="600"
            color="gray.800"
            mb={{ base: 1, sm: 1 }}
          >
            {user.first_name} {user.last_name}
          </Text>
          <Text
            fontSize={{ base: 'xs', sm: 'xs', md: 'sm' }}
            color="gray.500"
            lineHeight="1.3"
          >
            Founder • Raising {formatCurrency(deal.target_amount)}
          </Text>
        </Box>
      </Flex>
      {/* Fundraising Status Banner */}
      <Box
        bg="green.50"
        border="1px solid"
        borderColor="green.200"
        borderRadius="lg"
        p={{ base: 3, sm: 3, md: 4 }}
        mb={{ base: 4, sm: 4, md: 5 }}
      >
        <Flex
          alignItems="center"
          justifyContent="center"
          flexDirection={{ base: 'column', sm: 'row' }}
          textAlign={{ base: 'center', sm: 'left' }}
          gap={{ base: 2, sm: 0 }}
        >
          <FaDollarSign
            color="#38A169"
            size={useBreakpointValue({ base: 14, sm: 16, md: 18 })}
          />
          <Text
            fontSize={{ base: 'xs', sm: 'sm', md: 'md' }}
            fontWeight="600"
            color="green.700"
            ml={{ base: 0, sm: 2 }}
            mt={{ base: 0, sm: 0 }}
            lineHeight="1.4"
            textAlign={{ base: 'center', sm: 'left' }}
          >
            {user.first_name} is raising {formatCurrency(deal.target_amount)} in{' '}
            {deal.stage} funding
          </Text>
        </Flex>
      </Box>
      {/* Compact Deal Metrics - Horizontal on Mobile */}
      <Box bg="gray.50" p={{ base: 2, sm: 3 }} borderRadius="md" mb={3}>
        <Flex
          justifyContent="space-between"
          alignItems="center"
          gap={{ base: 1, sm: 2 }}
          flexDirection="row" // Force horizontal layout on all screen sizes
        >
          <Box textAlign="center" flex={1}>
            <Text
              fontSize={{ base: 'xs', sm: 'xs' }}
              color="gray.500"
              fontWeight="500"
              textTransform="uppercase"
              letterSpacing="0.5px"
              mb={1}
            >
              Target
            </Text>
            <Text fontSize={metricsFontSize} fontWeight="700" color="gray.800">
              {formatCurrency(deal.target_amount)}
            </Text>
          </Box>

          <Box
            w="1px"
            h="30px"
            bg="gray.300"
            display={{ base: 'block', sm: 'block' }}
          />

          <Box textAlign="center" flex={1}>
            <Text
              fontSize={{ base: 'xs', sm: 'xs' }}
              color="gray.500"
              fontWeight="500"
              textTransform="uppercase"
              letterSpacing="0.5px"
              mb={1}
            >
              Min
            </Text>
            <Text fontSize={metricsFontSize} fontWeight="700" color="gray.800">
              {formatCurrency(deal.min_ticket_size)}
            </Text>
          </Box>

          <Box
            w="1px"
            h="30px"
            bg="gray.300"
            display={{ base: 'block', sm: 'block' }}
          />

          <Box textAlign="center" flex={1}>
            <Text
              fontSize={{ base: 'xs', sm: 'xs' }}
              color="gray.500"
              fontWeight="500"
              textTransform="uppercase"
              letterSpacing="0.5px"
              mb={1}
            >
              Closes
            </Text>
            <Text fontSize={metricsFontSize} fontWeight="700" color="gray.800">
              {moment(deal.close_date).format('MMM DD')}
            </Text>
          </Box>
        </Flex>
      </Box>

      {/* Call to Action */}
      <Button
        w="100%"
        size={buttonSize}
        bg="#134A70"
        color="white"
        borderRadius="lg"
        fontWeight="600"
        fontSize={{ base: 'sm', sm: 'md', md: 'lg' }}
        py={{ base: 3, sm: 3, md: 4 }}
        _hover={{
          bg: '#0f3a5a',
          transform: 'translateY(-1px)',
          boxShadow: 'lg',
        }}
        _active={{ bg: '#0a2a40' }}
        onClick={handleSubmitTermsheet}
        transition="all 0.2s ease"
        leftIcon={
          <FaDollarSign
            size={useBreakpointValue({ base: 14, sm: 16, md: 18 })}
          />
        }
      >
        View Investment Details
      </Button>
    </Box>
  );
};

export default DealCard;
